import { useCallback, useEffect, useRef, useState } from "react";
import {
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  Table,
  TableHead,
  Box,
  CircularProgress,
} from "@mui/material";
import Iconify from "../iconify/Iconify";
import { REWARD_TYPES } from "src/utils/rewardTypesUtils";
import { get } from "src/utils/networkUtils";

const convertRewardType = (type) => REWARD_TYPES[type] || "Unknown";
const convertRewardSource = (source) => {
  switch (source) {
    case 0:
      return "Coins";
    case 1:
      return "XP";
    case 2:
      return "Hammers";
    default:
      return "Unknown";
  }
};
export default function RewardsTable(props) {
  const { userId, rewardType } = props;
  // promo rewards (promotion coins, Hammer Smash hammers) also show which promo and when hammers expire
  const isPromo = rewardType === "promo";
  const [rewards, setRewards] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const observer = useRef();

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    get(`/admin/users/${userId}/rewards?rewardType=${rewardType}&offset=${offset}`)
      .then((response) => {
        const newRewards = response.rewards || [];
        setRewards((prev) => (offset === 0 ? newRewards : [...prev, ...newRewards]));
        setHasMore(newRewards.length > 0);
      })
      .catch((error) => console.error("Error fetching rewards:", error))
      .finally(() => setLoading(false));
  }, [userId, rewardType, offset]);

  const lastRowRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setOffset(rewards.length);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, rewards.length]
  );

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell>Transaction ID</TableCell>
            <TableCell>User ID</TableCell>
            <TableCell>Type</TableCell>
            {isPromo && <TableCell>Promo</TableCell>}
            <TableCell>Reward</TableCell>
            {isPromo && <TableCell>Expires</TableCell>}
            <TableCell>Is New</TableCell>
            <TableCell>Updated At</TableCell>
            <TableCell>Created At</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rewards.map((row, index) => (
            <TableRow
              key={index}
              ref={index === rewards.length - 1 ? lastRowRef : null}
              sx={{
                "&:last-child td, &:last-child th": { border: 0 },
              }}
            >
              <TableCell component="th" scope="row">
                {row.rewardId}
              </TableCell>
              <TableCell component="th" scope="row">
                {row.userId}
              </TableCell>

              <TableCell component="th" scope="row">
                {convertRewardType(row.rewardType)}
              </TableCell>
              {isPromo && (
                <TableCell component="th" scope="row">
                  {row.title || "-"}
                  {row.hammerSmashCampaignId ? ` (#${row.hammerSmashCampaignId})` : row.promotionId ? ` (#${row.promotionId})` : ""}
                </TableCell>
              )}
              <TableCell component="th" scope="row">
                {row.rewardAmount} {convertRewardSource(row.rewardSource)}
              </TableCell>
              {isPromo && (
                <TableCell component="th" scope="row">
                  {row.expiresAt ? row.expiresAt.substring(0, 10) : "-"}
                </TableCell>
              )}
              <TableCell component="th" scope="row">
                {row.isNew ? (
                  <Iconify
                    icon={"eva:checkmark-circle-2-outline"}
                    sx={{ color: "green" }}
                  />
                ) : (
                  <Iconify
                    icon={"eva:close-circle-outline"}
                    sx={{ color: "red" }}
                  />
                )}
              </TableCell>
              <TableCell component="th" scope="row">
                {row.updatedAt.substring(0, 10)}
              </TableCell>
              <TableCell component="th" scope="row">
                {row.createdAt.substring(0, 10)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </TableContainer>
  );
}
